import React, { useCallback, useReducer, useState, useEffect, useRef } from 'react';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import {
  Button,
  Col,
  Form,
  Row,
  UncontrolledAlert,
} from 'reactstrap';
import { createQuotationAdminEndpoint, getAdminModelUnitEndpoint, getQuotationConfig } from '../services/ModelUnitService';
import useAuth from '../../../hooks/useAuth';
import useAdminEntity from '../../../hooks/useAdminQuotationEntity';
import { FinanceAction, FinanceState, Props } from '../interfaces/form.interface';
import { ModelUnitResponse } from '../interfaces/api-responses.interface';
import { AsesorInfo } from '../interfaces/asesor.interface';
import { ClientInfo } from '../interfaces/client.interface';
import { QuotationPayload } from '../interfaces/quotation.interface';
import { UnitInfo } from '../interfaces/unit.interface';
import { ConsiderationsInfo } from '../interfaces/considerations.interface'; // AGREGADO

import UnitInformationQuotationComponent from './UnitInformationQuotationComponent';
import AsesorInformationQuotationComponent from './AsesorInformationQuotationComponent';
import ClientInformationQuotationComponent from './ClientInformationQuotationComponent';
import PaymentInformationQuotationComponent, { generatePaymentDetailTable } from './PaymentInformationQuotationComponent';
import ConsiderationsQuotationComponent from './ConsiderationsQuotationComponent'; // AGREGADO

// ============ CONSTANTS ============
const INITIAL_STATE: FinanceState = {
  pctToCollect: 2,
  pctReservePayment: 7,
  pctDownPayment: 3,
  pctEscrituracion: 88,
  pctDiscount: 0,
  amountToCollect: 0,
  amountReservePayment: 0,
  amountDownPayment: 0,
  amountEscrituracion: 0,
  amountDiscount: 0,
  modelUnitBasePrice: 0,
  totalPercentage: 100,
  totalAmount: 0,
  discountName: '',
  reservaDate: null,
  firmaPromesaDate: null,
  complementoPrimaDate: null,
  escrituracionDate: null,
  discountDate: null,
  
  // AGREGADO: Nuevos campos de tipo de pago
  paymentTypeReserva: 'único pago',
  paymentTypeFirma: 'único pago',
  paymentTypeComplemento: 'único pago',
  paymentTypeEscrituracion: 'único pago',
  
  clientInfo: { nombre: '', email: '', telefono: '', direccion: '' },
  asesorInfo: { nombre: '', email: '' },
  unitInfo: { projectName: '', unitName: '', basePrice: 0, unitView: '' },
  considerationsInfo: { summaryNotes: '' }, // AGREGADO
};

// ============ UTILITIES ============
const calculateTotals = (state: FinanceState): FinanceState => ({
  ...state,
  totalPercentage: state.pctToCollect + state.pctDownPayment + state.pctReservePayment + state.pctEscrituracion + state.pctDiscount,
  totalAmount: state.amountToCollect + state.amountDownPayment + state.amountReservePayment + state.amountEscrituracion + state.amountDiscount,
});

// MODIFICADO: Calcular porcentajes desde montos con 2 decimales
const calculatePercentagesFromAmounts = (basePrice: number, state: FinanceState): FinanceState => {
  if (basePrice <= 0) return state;
  
  return {
    ...state,
    pctToCollect: Math.round((state.amountToCollect / basePrice) * 100 * 100) / 100,
    pctDownPayment: Math.round((state.amountDownPayment / basePrice) * 100 * 100) / 100,
    pctReservePayment: Math.round((state.amountReservePayment / basePrice) * 100 * 100) / 100,
    pctEscrituracion: Math.round((state.amountEscrituracion / basePrice) * 100 * 100) / 100,
    pctDiscount: Math.round((state.amountDiscount / basePrice) * 100 * 100) / 100,
  };
};

// NUEVA FUNCIÓN: Validar que el total no exceda el precio base
const validateTotalAmount = (state: FinanceState, newAmount: number, currentField: keyof FinanceState): number => {
  const basePrice = state.modelUnitBasePrice;
  if (basePrice <= 0) return newAmount;
  
  // Calcular el total actual sin el campo que está cambiando
  let totalWithoutCurrent = 0;
  const amountFields: (keyof FinanceState)[] = ['amountToCollect', 'amountDownPayment', 'amountReservePayment', 'amountEscrituracion', 'amountDiscount'];
  
  amountFields.forEach(field => {
    if (field !== currentField) {
      totalWithoutCurrent += state[field] as number;
    }
  });
  
  // Verificar si el nuevo monto haría que se exceda el precio base
  const maxAllowedAmount = basePrice - totalWithoutCurrent;
  
  // Retornar el menor entre el nuevo monto y el máximo permitido
  return Math.max(0, Math.min(newAmount, maxAllowedAmount));
};

// Función existente sin cambios
const recalculateAmounts = (basePrice: number, state: FinanceState): FinanceState => ({
  ...state,
  modelUnitBasePrice: basePrice,
  amountToCollect: (basePrice * state.pctToCollect / 100),
  amountDownPayment: (basePrice * state.pctDownPayment / 100),
  amountReservePayment: (basePrice * state.pctReservePayment / 100),
  amountEscrituracion: (basePrice * state.pctEscrituracion / 100),
  amountDiscount: (basePrice * state.pctDiscount / 100),
});

const formatDisplayValue = (value: string | number, type?: string): string | number => {
  return type === 'number' && value === 0 ? '' : value;
};

// Función para formatear fecha a formato "sep-25"
const formatDateForAPI = (date: Date | null): string => {
  if (!date) return '';
  
  const monthNames = ['ene', 'feb', 'mar', 'abr', 'may', 'jun','jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2);
  
  return `${month}-${year}`;
};

// Helper para descargar un PDF a partir de un string base64
const downloadPDFFromBase64 = (base64: string, fileName = 'download.pdf'): void => {
  try {
    // Si el base64 viene con prefijo data:application/pdf;base64, lo removemos
    const cleanedBase64 = base64.split(',').pop() || '';
    const byteCharacters = atob(cleanedBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    console.error('Error downloading PDF from base64:', e);
    throw e;
  }
};

// Helper para descargar un DOCX a partir de un string base64
const downloadDocxFromBase64 = (base64: string, fileName = 'download.docx'): void => {
  try {
    // Si el base64 viene con prefijo data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64, lo removemos
    const cleanedBase64 = base64.split(',').pop() || '';
    const byteCharacters = atob(cleanedBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    console.error('Error downloading Docx from base64:', e);
    throw e;
  }
};

// ============ REDUCER ============
const financeReducer = (state: FinanceState, action: FinanceAction): FinanceState => {
  switch (action.type) {
    case 'updateField': {
      const fieldStr = action.field.toString();
      
      // Handle nested info objects
      if (fieldStr.includes('_')) {
        const [prefix, field] = fieldStr.split('_');
        if (['client', 'asesor', 'unit', 'considerations'].includes(prefix)) { // AGREGADO: considerations
          const infoKey = `${prefix}Info` as keyof FinanceState;
          const currentInfo = state[infoKey] as any;
          
          let value = action.value;
          if (prefix === 'unit' && field === 'basePrice') {
            value = action.value === '' || action.value === null ? 0 : parseFloat(action.value as string || '0');
            const newState = {
              ...state,
              [infoKey]: { ...currentInfo, [field]: value },
              modelUnitBasePrice: value as number
            };
            return calculateTotals(recalculateAmounts(value as number, newState));
          }
          
          return {
            ...state,
            [infoKey]: { ...currentInfo, [field]: value }
          };
        }
      }
      
      // NUEVO: Handle amount updates (calcular porcentajes desde montos)
      if (fieldStr.startsWith('amount') && typeof action.value === 'number') {
        const newAmount = validateTotalAmount(state, action.value, action.field as keyof FinanceState);
        const newState = { ...state, [action.field]: newAmount } as FinanceState;
        const stateWithPercentages = calculatePercentagesFromAmounts(state.modelUnitBasePrice, newState);
        
        return calculateTotals(stateWithPercentages);
      }
      
      // MODIFICADO: Handle percentage updates con 2 decimales
      if (fieldStr.startsWith('pct') && typeof action.value === 'number') {
        // Redondear el porcentaje de entrada a 2 decimales
        const roundedPercentage = Math.round(action.value * 100) / 100;
        const newState = { ...state, [action.field]: roundedPercentage } as FinanceState;
        const basePrice = state.modelUnitBasePrice;
        
        const pctToAmountMap: Record<string, keyof FinanceState> = {
          pctToCollect: 'amountToCollect',
          pctDownPayment: 'amountDownPayment',
          pctReservePayment: 'amountReservePayment',
          pctEscrituracion: 'amountEscrituracion',
          pctDiscount: 'amountDiscount',
        };
        
        const amountKey = pctToAmountMap[fieldStr];
        if (amountKey) {
          // Calcular el nuevo monto basado en el porcentaje
          const calculatedAmount = basePrice * roundedPercentage / 100;
          // Validar que no exceda el límite
          const validatedAmount = validateTotalAmount(state, calculatedAmount, amountKey);
          (newState as any)[amountKey] = validatedAmount;
          
          if (validatedAmount !== calculatedAmount && basePrice > 0) {
            // Recalcular porcentaje con 2 decimales si el monto fue validado
            (newState as any)[action.field] = Math.round((validatedAmount / basePrice) * 100 * 100) / 100;
          }
        }
        
        return calculateTotals(newState);
      }
      
      const newState = { ...state, [action.field]: action.value };
      return calculateTotals(newState);
    }
    
    case 'setInfo': {
      const { infoType, data } = action;
      const infoKey = `${infoType}Info` as keyof FinanceState;
      
      if (infoType === 'unit') {
        const unitData = data as UnitInfo;
        const newState = {
          ...state,
          [infoKey]: unitData,
          modelUnitBasePrice: unitData.basePrice
        };
        return calculateTotals(recalculateAmounts(unitData.basePrice, newState));
      }
      
      return { ...state, [infoKey]: data };
    }
    
    default:
      return state;
  }
};

// ============ MAIN COMPONENT ============
const ManageModelUnitQuotationForm: React.FC<Props> = ({
  handleForm,
  projectModelId,
  modelUnitId,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [symbolData, setSymbolData] = useState('');
  const [financeState, dispatch] = useReducer(financeReducer, INITIAL_STATE);
  const [shouldCreateQuotation, setShouldCreateQuotation] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();

  const [paymentFields, setPaymentFields] = useState<any[]>([]);

  useEffect(() => {
    const fetchPaymentFields = async () => {
      try {
        const { data } = await callEndpoint(getQuotationConfig(getAuthToken()));
        setPaymentFields(Array.isArray(data.data?.payment_fields) ? data.data.payment_fields : []);
      } catch {
        setPaymentFields([]);
      }
    };
    fetchPaymentFields();
    // Solo se ejecuta una vez al montar el componente
    // eslint-disable-next-line
  }, []);

  // ============ HANDLERS ============
  const handleError = useCallback((errorMessage: string) => {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }

    setError(errorMessage);
    
    errorTimeoutRef.current = setTimeout(() => {
      setError(null);
      errorTimeoutRef.current = null;
    }, 5000);
  }, []);

  const clearError = useCallback(() => {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    setError(null);
  }, []);

  // ============ UNIT DATA LOADING ============
  const getModelUnitData = useCallback(
    () => {
      return getAdminModelUnitEndpoint(projectModelId, modelUnitId, getAuthToken());
    },
    [projectModelId, modelUnitId, getAuthToken]
  );

  const setModelUnitInfo = useCallback(
    (data: ModelUnitResponse) => {
      
      const currencySymbol = data.project_currency === 'USD' ? '$' : data.project_currency;
      setSymbolData(currencySymbol);

      const unitInfo: UnitInfo = {
        projectName: data.project_name,
        unitName: data.unit_name,
        basePrice: data.unit_base_price,
        unitView: ''
      };
      
      dispatch({ type: 'setInfo', infoType: 'unit', data: unitInfo });
    },
    []
  );

  const handleModelUnitError = useCallback(
    (errorMessage: string) => {
      console.error('Error al cargar datos de la unidad:', errorMessage);
      handleError('Error al cargar información de la unidad');
    },
    [handleError]
  );

  const [, , isLoadingUnit] = useAdminEntity<ModelUnitResponse>(
    getModelUnitData,
    handleModelUnitError,
    setModelUnitInfo
  );

  // ============ QUOTATION CREATION ============
  /**
   * Build the payload sent to the create quotation endpoint from the financeState.
   * Uses the centralized generatePaymentDetailTable function for consistency.
   */
  const createQuotationPayload = (state: FinanceState, modelUnitId: string, symbol: string): any => {
    return {
      unit_id: modelUnitId,
      landlord_id: (state.clientInfo as any).landlord_id || '',
      proper_id: (state.asesorInfo as any).proper_id || '',
      custom_properties: {
        unit_view: state.unitInfo.unitView || '',
        client_address: state.clientInfo.direccion || '',
        summary_notes: state.considerationsInfo.summaryNotes || '' // AGREGADO
      },
      payment_detail_table: generatePaymentDetailTable(state, symbol, paymentFields)
    };
  };

  const getCreateQuotationData = useCallback(() => {
    if (!shouldCreateQuotation) {
      return null;
    }
  
    try {
      const quotationPayload = createQuotationPayload(financeState, modelUnitId, symbolData);
      
      const baseEndpoint = createQuotationAdminEndpoint(projectModelId, getAuthToken());
      
      const finalEndpoint = {
        ...baseEndpoint,
        call: (urlService: string) => {
          return baseEndpoint.call(urlService, quotationPayload);
        }
      };
      
      return finalEndpoint;
    } catch (error) {
      return null;
    }
  }, [shouldCreateQuotation, financeState, modelUnitId, projectModelId, getAuthToken, symbolData]);

  useEffect(() => {
    const endpoint = getCreateQuotationData();
  }, [getCreateQuotationData]);

  const setQuotationCreated = useCallback(
    (response: any) => {
      
      // Resetear el estado de creación
      setShouldCreateQuotation(false);
      
      try {
        // Verificar múltiples estructuras posibles de respuesta
        let quotationData = null;
        let fileName = '';
        let fileBase64 = '';

        // Estructura 1: response.data.data.data (la esperada)
        if (response?.data?.data?.data) {
          quotationData = response.data.data.data;
          fileName = quotationData.fileName || '';
          fileBase64 = quotationData.fileBase64 || '';
        }
        // Estructura 2: response.data.data (alternativa)
        else if (response?.data?.data) {
          quotationData = response.data.data;
          fileName = quotationData.fileName || '';
          fileBase64 = quotationData.fileBase64 || '';
        }
        // Estructura 3: response.data (alternativa)
        else if (response?.data) {
          quotationData = response.data;
          fileName = quotationData.fileName || '';
          fileBase64 = quotationData.fileBase64 || '';
        }
        // Estructura 4: response directa
        else if (response) {
          quotationData = response;
          fileName = quotationData.fileName || '';
          fileBase64 = quotationData.fileBase64 || '';
        }

        // Si hay archivo base64, descargarlo
        if (fileBase64 && fileBase64.trim() !== '') {
          setIsDownloadingPDF(true);
          
          try {
            downloadDocxFromBase64(fileBase64, fileName || 'cotizacion.pdf');
            handleError('¡Cotización creada y PDF descargado exitosamente!');
          } catch (downloadError) {
            handleError('Cotización creada exitosamente, pero hubo un error al descargar el PDF');
          } finally {
            setIsDownloadingPDF(false);
          }
        }
      } catch (error) {
        handleError('¡Cotización creada exitosamente!');
      }
      
      // Llamar al handler original si existe
      if (typeof handleForm === 'function') {
        handleForm(
          {
            percentage_reserve_payment: financeState.pctReservePayment,
            percentage_down_payment: financeState.pctDownPayment,
          },
          setError
        );
      }
    },
    [financeState.pctReservePayment, financeState.pctDownPayment, handleForm, handleError]
  );

  const handleCreateQuotationError = useCallback(
    (errorMessage: string) => {
      
      setShouldCreateQuotation(false);
      
      let userFriendlyError = 'Error al crear la cotización';
      
      handleError(userFriendlyError);
    },
    [handleError]
  );

  const [, , isCreatingQuotation] = useAdminEntity<any>(
    getCreateQuotationData,
    handleCreateQuotationError,
    setQuotationCreated
  );

  // ============ FORM HANDLERS CON MÁS LOGS ============
  const handleCreateQuotation = useCallback((event: React.SyntheticEvent) => {
    event.preventDefault();

    if (!financeState.clientInfo.nombre.trim()) {
      handleError('Debe ingresar la información del cliente');
      return;
    }
    
    if (!financeState.asesorInfo.nombre.trim()) {
      handleError('Debe ingresar la información del asesor');
      return;
    }
    
    if (!financeState.clientInfo.landlord_id) {
      handleError('Debe buscar y seleccionar un cliente válido usando el buscador');
      return;
    }
    
    if (!financeState.asesorInfo.proper_id) {
      handleError('Debe buscar y seleccionar un asesor válido usando el buscador');
      return;
    }

    if (financeState.totalAmount > financeState.modelUnitBasePrice) {
      handleError(`El total de montos (${symbolData}${financeState.totalAmount.toLocaleString()}) excede el precio base (${symbolData}${financeState.modelUnitBasePrice.toLocaleString()})`);
      return;
    }
    
    if (Math.abs(financeState.totalPercentage - 100) > 5) {
      handleError(`El total de porcentajes debe estar cerca del 100%. Actual: ${financeState.totalPercentage.toFixed(2)}%`);
      return;
    }

    const requiredDates = [
      { date: financeState.reservaDate, name: 'Fecha de reserva' },
      { date: financeState.firmaPromesaDate, name: 'Fecha de firma de promesa' },
      { date: financeState.complementoPrimaDate, name: 'Fecha de complemento prima' },
      { date: financeState.escrituracionDate, name: 'Fecha de escrituración' }
    ];

    for (const { date, name } of requiredDates) {
      if (!date) {
        handleError(`Debe seleccionar la ${name.toLowerCase()}`);
        return;
      }
    }
    
    setShouldCreateQuotation(true);
  }, [financeState, handleError, symbolData]);

  // ============ INPUT HANDLERS ============
  const handleInputChange = useCallback((field: string | number | symbol, value: string) => {
    const key = field as keyof FinanceState;
    
    if (key === 'discountName') {
      dispatch({ type: 'updateField', field: key, value });
    } else {
      const numericValue = value === '' ? 0 : parseFloat(value || '0');
      dispatch({ type: 'updateField', field: key, value: numericValue });
    }
  }, []);

  const handleDateChange = useCallback((field: string | number | symbol, date: Date) => {
    const key = field as keyof FinanceState;
    dispatch({ type: 'updateField', field: key, value: date });
  }, []);

  const handleClientChange = useCallback((field: keyof ClientInfo, value: string) => {
    dispatch({ type: 'updateField', field: `client_${field}`, value });
  }, []);

  const handleAsesorChange = useCallback((field: keyof AsesorInfo, value: string) => {
    dispatch({ type: 'updateField', field: `asesor_${field}`, value });
  }, []);

  const handleUnitChange = useCallback((field: keyof UnitInfo, value: string) => {
    const finalValue = field === 'basePrice' ? parseFloat(value || '0') : value;
    dispatch({ type: 'updateField', field: `unit_${field}`, value: finalValue });
  }, []);

  const handleClientFound = useCallback((client: ClientInfo) => {
    dispatch({ type: 'setInfo', infoType: 'client', data: client });
    setError(null);
  }, []);

  const handleAsesorFound = useCallback((asesor: AsesorInfo) => {
    dispatch({ type: 'setInfo', infoType: 'asesor', data: asesor });
    setError(null);
  }, []);

  // AGREGADO: Handler para consideraciones
  const handleConsiderationsChange = useCallback((field: keyof ConsiderationsInfo, value: string) => {
    dispatch({ type: 'updateField', field: `considerations_${field}`, value });
  }, []);

  // AGREGADO: Handler para tipos de pago
  const handlePaymentTypeChange = useCallback((field: string, value: string) => {
    dispatch({ type: 'updateField', field, value });
  }, []);

  useEffect(() => {
    if (shouldCreateQuotation) {
    }
  }, [shouldCreateQuotation]);

  useEffect(() => {
    if (isCreatingQuotation) {
    } else if (isCreatingQuotation === false && shouldCreateQuotation === false) {
    }
  }, [isCreatingQuotation]);

  return (
    <Form role="form" onSubmit={handleCreateQuotation}>
      {error && (
        <Row>
          <Col md="8">
            <UncontrolledAlert 
              color={error.includes('exitosamente') ? 'success' : 'danger'}
              fade={true}
              toggle={clearError}
            >
              <span className="alert-inner--text">{error}</span>
            </UncontrolledAlert>
          </Col>
        </Row>
      )}
      
      {/* Información de la Unidad */}
      <UnitInformationQuotationComponent
        unitInfo={financeState.unitInfo}
        symbolData={symbolData}
        onUnitChange={handleUnitChange}
      />
      
      {/* Información del Cliente */}
      <ClientInformationQuotationComponent
        clientInfo={financeState.clientInfo}
        onClientChange={handleClientChange}
        onClientFound={handleClientFound}
        onError={handleError}
        token={getAuthToken()}
      />
      
      {/* Información de Pagos */}
      <PaymentInformationQuotationComponent
        financeState={financeState}
        symbolData={symbolData}
        onInputChange={handleInputChange}
        onDateChange={handleDateChange}
        onPaymentTypeChange={handlePaymentTypeChange}
        paymentFields={paymentFields}
      />
      
      {/* AGREGADO: Consideraciones */}
      <ConsiderationsQuotationComponent
        considerationsInfo={financeState.considerationsInfo}
        onConsiderationsChange={handleConsiderationsChange}
      />
      
      {/* Información del Asesor */}
      <AsesorInformationQuotationComponent
        asesorInfo={financeState.asesorInfo}
        onAsesorChange={handleAsesorChange}
        onAsesorFound={handleAsesorFound}
        onError={handleError}
        token={getAuthToken()}
      />
      
      <Button 
        color="primary" 
        disabled={isLoading || isCreatingQuotation || isLoadingUnit || isDownloadingPDF}
        style={{ marginTop: '20px' }}
        type="submit"
      >
        {isDownloadingPDF ? '📄 Descargando PDF...' :
         isCreatingQuotation ? '⏳ Creando Cotización...' : 
         isLoadingUnit ? '📊 Cargando Unidad...' : 
         '🚀 Crear Cotización'}
      </Button>
    </Form>
  );
};

export default ManageModelUnitQuotationForm;
